import { supabase } from '@/lib/supabase';

export type CouponType = 'percent' | 'fixed' | 'free_months' | 'override_price';

export interface Coupon {
  id: string;
  code: string;
  type: CouponType;
  value: number;
  scope: string;
  max_redemptions: number | null;
  valid_from: string;
  valid_until: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  is_active: boolean;
}

export interface CouponRedemption {
  id: string;
  coupon_id: string;
  code: string;
  org_id: string;
  subscription_id: string | null;
  applied_price_agorot: number | null;
  redeemed_at: string;
  redeemed_by: string | null;
}

export interface CreateCouponPayload {
  code: string;
  type: CouponType;
  value: number;
  scope: string;
  maxRedemptions?: number | null;
  validFrom?: string | null;
  validUntil?: string | null;
  notes?: string | null;
}

export const couponsApi = {
  list: async (): Promise<Coupon[]> => {
    const { data, error } = await supabase
      .from('coupons')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as Coupon[];
  },

  create: async (payload: CreateCouponPayload): Promise<string> => {
    const { data, error } = await supabase.rpc('create_coupon', {
      p_code: payload.code,
      p_type: payload.type,
      p_value: payload.value,
      p_scope: payload.scope,
      p_max_redemptions: payload.maxRedemptions ?? null,
      p_valid_from: payload.validFrom ?? null,
      p_valid_until: payload.validUntil ?? null,
      p_notes: payload.notes ?? null,
    });
    if (error) throw error;
    return data as string;
  },

  deactivate: async (id: string): Promise<void> => {
    const { error } = await supabase.rpc('deactivate_coupon', { p_coupon_id: id });
    if (error) throw error;
  },

  listRedemptions: async (couponId?: string, limit = 20): Promise<CouponRedemption[]> => {
    let q = supabase
      .from('coupon_redemptions')
      .select('*')
      .order('redeemed_at', { ascending: false })
      .limit(limit);
    if (couponId) q = q.eq('coupon_id', couponId);
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []) as CouponRedemption[];
  },

  redeem: async (
    code: string,
    orgId: string,
    subscriptionId: string,
  ): Promise<{ new_price_agorot: number; coupon_code: string }> => {
    const { data, error } = await supabase.rpc('redeem_coupon', {
      p_code: code,
      p_org_id: orgId,
      p_subscription_id: subscriptionId,
    });
    if (error) throw error;
    return data as { new_price_agorot: number; coupon_code: string };
  },
};
